import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useAppSelector } from "../../../state/store";
import { DataTable } from "../../../components/DataTable/DataTable";
import { Row } from "../../../components/Row";
import { StatsSettings } from "../../../components/SKComponents/StatsSettings";
import { Check } from "../../../components/Check/Check";
import * as skTypes from "../../../types/sk";
import * as statsUtils from "../../../util/skStatsUtils";
import { addSeasonToLocalStore, getLocalSeason } from "../../../util/skUtils";
import { getPerAmount } from "../../../util/generalUtil";
import { useDataProvider } from "../../../hooks/dataProvider";
import { useKeyedParams } from "../../../hooks/useParams";
import {
  getSeasonInfo,
  setSelectedSeason,
} from "../../../state/actions/skActions";
import { useDocumentTitle } from "../../../hooks/useDocumentTitle";
import { Link } from "react-router";

interface QuizzerStatsProps {
  seasonId: string;
}

export function QuizzerStats() {
  const { seasonId } = useKeyedParams<QuizzerStatsProps>();
  const provider = useDataProvider({
    getData: getSeasonInfo,
    selector: (state) => state.sk.selectedSeason,
    dispatchAction: setSelectedSeason,
    id: seasonId,
    getId: (season) => season.seasonId,
    render: (season) => {
      return <QuizzerStatsComponent selectedSeason={season} />;
    },
    fallbackUrl: "/sk/seasons",
    cacheDetails: {
      prefix: "season",
      get: getLocalSeason,
      save: addSeasonToLocalStore,
    },
  });
  return provider.getPage();
}

export function QuizzerStatsComponent({
  selectedSeason,
}: {
  selectedSeason: skTypes.Season;
}) {
  useDocumentTitle("Quizzer Stats - Bible Quiz Academy");
  type SortProperty =
    | keyof skTypes.QuizzerStats
    | "seatTime"
    | "wordPercentage"
    | "jumpLuck"
    | "answerLuck";
  const location = useLocation();
  const locationState =
    location.state ||
    ({} as {
      settings?: skTypes.StatsSettings;
      sortProperty?: SortProperty;
      sortReversed?: boolean;
      fromQuiz?: string;
    });
  const navigate = useNavigate();
  const { seasonId } = selectedSeason;
  const { selectedEvent } = useAppSelector((state) => state.sk);
  const { skIncludeOvertime, skAdvanced } = useAppSelector(
    (state) => state.settings
  );
  const { material } = useAppSelector((state) => state.material);

  const [perQuiz, setPerQuiz] = useState<boolean>(false);
  const [stats, setStats] = useState<skTypes.QuizzerStats[]>([]);
  const [books, setBooks] = useState<number[]>([]);
  const settings = useMemo<skTypes.StatsSettings>(() => {
    return (
      locationState?.settings ||
      statsUtils.getEmptyStatsSettings(skIncludeOvertime)
    );
  }, [locationState, skIncludeOvertime]);

  const setSettings = (newSettings: skTypes.StatsSettings): void => {
    navigate(location.pathname, {
      state: {
        ...locationState,
        settings: newSettings,
      },
      replace: true,
    });
  };
  const [settingsVisible, setSettingsVisible] = useState<boolean>(false);

  const goBack = (): void => {
    navigate(
      locationState?.fromQuiz
        ? locationState.fromQuiz
        : `/sk/${seasonId}/${selectedEvent || "events"}`
    );
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    const { stats: statsObjects, books: newBookList } =
      statsUtils.getQuizzerStats(material, selectedSeason, settings);
    if (!statsObjects.length) {
      goBack();
    } else {
      setStats(statsObjects);
      if (!books.length) setBooks(newBookList);
    }
  }, []);

  if (settingsVisible) {
    return (
      <div className="page" style={{ minWidth: 700 }}>
        <StatsSettings
          purpose="quizzer"
          seasonId={seasonId}
          onGoBack={(newSettings: skTypes.StatsSettings) => {
            if (!selectedSeason) return;
            const { stats: statsObjects } = statsUtils.getQuizzerStats(
              material,
              selectedSeason,
              newSettings
            );
            setStats(Object.values(statsObjects));
            setSettings(newSettings);
            setSettingsVisible(false);
          }}
          defaultSettings={settings}
          books={books}
          isPractice={selectedSeason?.isPractice}
        />
      </div>
    );
  }
  return (
    <div className="page" style={{ minWidth: 700 }}>
      <Row style={{ marginBottom: 10, position: "relative" }}>
        <button className="secondary-btn" onClick={goBack}>
          Go Back
        </button>
        <h1 style={{ marginLeft: 10 }}>Quizzer Stats</h1>
        <Row style={{ position: "absolute", right: 0 }}>
          <Check
            checked={perQuiz}
            onClick={setPerQuiz}
            style={{ width: 150, marginTop: -3 }}
          >
            Show Per Quiz
          </Check>
          <button
            className="clickable"
            style={{ marginLeft: 10 }}
            onClick={() => setSettingsVisible(true)}
          >
            Settings
          </button>
        </Row>
      </Row>
      <DataTable<skTypes.QuizzerStats>
        data={[...stats, statsUtils.getTotalQuizzerStats(stats)]}
        keysAtBottom={["total"]}
        getKey={(obj) => obj.quizzerId}
        defaultSort={{
          property: "quizCount",
          direction: "desc",
        }}
        columns={[
          {
            title: "Name",
            property: "name",
            render: (obj) => {
              if (!skAdvanced) return obj.quizzerName;
              return (
                <Link
                  className="link"
                  to={`/sk/${seasonId}/quizzerstats/${obj.quizzerId}`}
                  state={{ quizzerStats: obj }}
                  aria-label={`Stats for ${obj.quizzerName}`}
                >
                  {obj.quizzerName}
                </Link>
              );
            },
            sortFunc: true,
          },
          {
            title: "Team",
            property: "teamName",
            visible: !selectedSeason?.isPractice,
            render: (obj) => obj.teamName,
            sortFunc: true,
          },
          {
            title: "Quizzes",
            property: "quizCount",
            render: (obj) => obj.quizzes.size,
            sortFunc: true,
            visible: !locationState.fromQuiz || settings.quizIds.size !== 1,
          },
          {
            title: perQuiz ? "Pts/Quiz (Crt)" : "Points (Crt)",
            property: "points",
            render: (obj) =>
              `${
                perQuiz
                  ? getPerAmount(obj.points, obj.quizzes.size)
                  : obj.points
              } (${
                perQuiz
                  ? getPerAmount(obj.correct, obj.quizzes.size)
                  : obj.correct
              })`,
            sortFunc: (obj) =>
              perQuiz ? obj.points / obj.quizzes.size : obj.points,
          },
          {
            title:
              (perQuiz ? "Ers/Quiz" : "Errors") + (skAdvanced ? " (K/L)" : ""),
            property: "errors",
            render: (obj) => {
              const mainPart = perQuiz
                ? obj.quizzerId === "total"
                  ? "-"
                  : getPerAmount(obj.errors, obj.quizzes.size)
                : obj.errors;
              if (!skAdvanced || obj.quizzerId === "total") return mainPart;
              const kErrors = obj.questionsByType.K || 0;
              const lErrors = obj.questionsByType["L"] || 0;
              return `${mainPart} (${
                perQuiz ? getPerAmount(kErrors, obj.quizzes.size) : kErrors
              }/${
                perQuiz ? getPerAmount(lErrors, obj.quizzes.size) : lErrors
              })`;
            },
            sortFunc: (obj) =>
              perQuiz ? obj.errors / obj.quizzes.size : obj.errors,
          },
          {
            title: "Seat Time (Abs.)",
            property: "seatTime",
            visible: !selectedSeason?.isPractice,
            render: (obj) => {
              if (obj.seatTimeEligible === 0) return "-";
              return `${Math.round(
                (obj.questionsOnSeat * 100) / obj.seatTimeEligible
              )}% (${Math.round(
                (obj.questionsOnSeat * 100) / obj.totalPossibleQuestions
              )}%)`;
            },
            sortFunc: (obj) =>
              obj.seatTimeEligible === 0
                ? 0
                : obj.questionsOnSeat / obj.seatTimeEligible,
          },
          {
            title: "Word %",
            property: "wordPercentage",
            visible: skAdvanced,
            render: (obj) => {
              const wordPercentage = statsUtils.getWordPercentage(
                obj.jumpsByType
              );
              return isNaN(wordPercentage) ? "-" : `${wordPercentage}%`;
            },
            sortFunc: (obj) => {
              const wordPercentage = statsUtils.getWordPercentage(
                obj.jumpsByType
              );
              return isNaN(wordPercentage) ? 0 : wordPercentage;
            },
          },
          {
            title: "Jump Luck",
            property: "jumpLuck",
            visible: skAdvanced,
            render: (obj) =>
              obj.luck.expectedFromJumps === 0
                ? "-"
                : `${Math.round(
                    (obj.luck.actual * 100) / obj.luck.expectedFromJumps
                  )}%`,
            sortFunc: (obj) =>
              obj.luck.expectedFromJumps === 0
                ? 0
                : obj.luck.actual / obj.luck.expectedFromJumps,
          },
          {
            title: "Answer Luck",
            property: "answerLuck",
            visible: skAdvanced,
            render: (obj) =>
              obj.luck.expectedFromQuestions === 0
                ? "-"
                : `${Math.round(
                    (obj.luck.choicesCorrect * 100) /
                      obj.luck.expectedFromQuestions
                  )}%`,
            sortFunc: (obj) =>
              obj.luck.expectedFromQuestions === 0
                ? 0
                : obj.luck.choicesCorrect / obj.luck.expectedFromQuestions,
          },
        ]}
      />
    </div>
  );
}
